import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import {
  Button,
  TextField,
  FormControl,
  Grid,
  Breadcrumbs,
  Stack,
  Typography,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

// W-24 (owner decision 2026-10-07): one fabricated item per End Product name per job is the server
// rule, so the form creates exactly one item: one End Product, one quantity, one PO item. There are no
// extra rows to add (a second row with the same name was always refused).
const CreateFabrecatedItems = () => {
  const [row, setRow] = useState({ quantity: "", poitems_id: "" });
  const [endProduct, setEndProduct] = useState("");
  const [poItems, setPoItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const submittingRef = useRef(false); // blocks a second submit before the state update lands
  const { id } = useParams();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setSaveError(null);

    if (!endProduct) {
      toast.error("End Product is required", {
        position: "top-right",
      });
      return;
    }

    if (
      row.quantity === "" ||
      isNaN(row.quantity) ||
      row.quantity <= 0 ||
      !row.poitems_id
    ) {
      toast.error("Please enter a quantity and select a PO item", {
        position: "top-right",
      });
      return;
    }

    // Frozen copy of the validated form: edits made while saving cannot change what is sent
    // (the form is also disabled until saving ends).
    const payload = {
      name: endProduct,
      job_Id: id,
      quantity: row.quantity,
      poitems_id: row.poitems_id,
    };

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await ApiCall.post("/fabricated-items/create-fabricated-item", payload);
      showSuccessToast(`"${payload.name}" created`);
      navigate(`/detail-Job/${id}`);
    } catch (error) {
      // Shown as the server gives it, e.g. its 409 for an End Product name already on this job.
      const body = error.response?.data;
      const reason =
        (typeof body?.data === "string" && body.data) ||
        body?.message ||
        error.message ||
        "Not saved";
      showErrorToast(reason);
      setSaveError(reason);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    if (submittingRef.current) return;
    setSaveError(null);
    setRow((r) => ({ ...r, [fieldName]: value }));
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await ApiCall.get(
          `/fabricated-items/get-poitem-perjob/${id}`
        );
        setPoItems(res.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchItems();
  }, [id]);

  return (
    <>
      <div>
        <Helmet>
          <title>Create Fabricated Item | SFM</title>
        </Helmet>
        <container>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  Create Fabricated Item
                </Typography>
              </Stack>
            </Breadcrumbs>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                item
                xs={3}
                style={{ marginLeft: "30%", marginBottom: "2%" }}
              >
                <FormControl fullWidth>
                  <TextField
                    onChange={(event) => {
                      if (submittingRef.current) return;
                      setSaveError(null);
                      setEndProduct(event.target.value);
                    }}
                    name="endProduct"
                    label="End Product"
                    disabled={submitting}
                    required
                    value={endProduct}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2} />
              <Grid item xs={3} style={{ marginBottom: "5px" }}>
                <FormControl fullWidth>
                  <TextField
                    onChange={(event) =>
                      handleFieldChange("quantity", event.target.value)
                    }
                    name="quantity"
                    label="Quantity"
                    disabled={submitting}
                    value={row.quantity}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={3} style={{ marginBottom: "5px" }}>
                <FormControl fullWidth>
                  <InputLabel htmlFor="poitem-select">Select POItem</InputLabel>
                  <Select
                    disabled={submitting}
                    value={row.poitems_id}
                    onChange={(event) =>
                      handleFieldChange("poitems_id", event.target.value)
                    }
                    id="poitem-select"
                  >
                    {poItems.length === 0 && (
                      <MenuItem disabled value="">
                        No Item Found
                      </MenuItem>
                    )}
                    {poItems.map((dataItem) => (
                      <MenuItem key={dataItem.id} value={dataItem.id}>
                        {dataItem.itemName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {saveError && (
                <Grid item xs={4}>
                  <Typography variant="caption" color="error">
                    Not created: {saveError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Create"}
            </Button>
          </form>
        </container>
      </div>
    </>
  );
};

export default CreateFabrecatedItems;
